import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabaseAdmin } from "@/lib/supabaseAdmin";
import type { User } from "@supabase/supabase-js";
import AdminSidebar from "./components/AdminSidebar";
import AdminOverview from "./components/AdminOverview";
import AdminSupply from "./components/AdminSupply";
import AdminOrders from "./components/AdminOrders";
import AdminUsers from "./components/AdminUsers";
import AdminFinance from "./components/AdminFinance";
import AdminVerifications from "./components/AdminVerifications";

interface AdminProfile {
  id: string;
  name: string | null;
  username: string | null;
  phone: string | null;
  membership_level: string;
  account_status: string;
  created_at: string | null;
  is_admin: boolean;
}

const sectionTitles: Record<string, { title: string; subtitle: string }> = {
  overview: { title: "儀表板概覽", subtitle: "平台數據總覽與趨勢分析" },
  supply: { title: "貨源商品管理", subtitle: "新增、編輯或移除認購貨源" },
  orders: { title: "認購訂單", subtitle: "追蹤所有會員認購記錄" },
  users: { title: "會員資料中心", subtitle: "管理會員帳戶與權限資訊" },
  verifications: { title: "認證審核", subtitle: "審核會員實名認證與身分證件" },
  finance: { title: "提領審核", subtitle: "審核提現與入金申請" },
};

function usernameToEmail(username: string): string {
  const sanitized = username.trim().toLowerCase().replace(/\s+/g, "");
  if (sanitized.includes("@")) return sanitized;
  return `${sanitized}@users.local`;
}

function translateAuthError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes("invalid login credentials")) {
    return "帳號或密碼錯誤";
  }
  if (lower.includes("user not found")) {
    return "找不到此帳號，請先註冊";
  }
  if (lower.includes("email not confirmed")) {
    return "帳號尚未驗證，請查收確認郵件";
  }
  if (lower.includes("too many requests")) {
    return "登入嘗試過多，請稍後再試";
  }
  if (lower.includes("network request failed") || lower.includes("fetch")) {
    return "網路連線異常，請檢查網路後重試";
  }
  return message;
}

export default function AdminPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window !== "undefined" && location.pathname === "/admin/verifications") {
      return "verifications";
    }
    return "overview";
  });
  const [loginUsername, setLoginUsername] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [adminVerified, setAdminVerified] = useState(() => {
    if (typeof window === "undefined") return false;
    return sessionStorage.getItem("admin_verified") === "true";
  });

  // ── Admin auth state (isolated from frontend via sessionStorage) ──
  const [adminUser, setAdminUser] = useState<User | null>(null);
  const [adminProfile, setAdminProfile] = useState<AdminProfile | null>(null);
  const [adminLoading, setAdminLoading] = useState(true);
  const [profileResolved, setProfileResolved] = useState(false);

  const titleInfo = sectionTitles[activeTab] || sectionTitles.overview;

  const fetchAdminProfile = useCallback(async (userId: string) => {
    setProfileResolved(false);
    try {
      const { data, error } = await supabaseAdmin
        .from("profiles")
        .select("id, name, username, phone, membership_level, account_status, created_at, is_admin")
        .eq("id", userId)
        .maybeSingle();
      if (error) {
        console.error("[AdminPage] fetchAdminProfile error:", error);
        return;
      }
      if (data) {
        setAdminProfile({
          id: data.id,
          name: data.name ?? null,
          username: data.username ?? null,
          phone: data.phone ?? null,
          membership_level: data.membership_level ?? "member",
          account_status: data.account_status ?? "active",
          created_at: data.created_at ?? null,
          is_admin: data.is_admin ?? false,
        });
      }
    } catch (err) {
      console.warn("[AdminPage] fetchAdminProfile exception:", err);
    } finally {
      setProfileResolved(true);
    }
  }, []);

  // One-time admin auth init
  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      setProfileResolved(false);
      try {
        const { data: sessionData, error: sessionError } = await supabaseAdmin.auth.getSession();
        if (sessionError) {
          console.warn("[AdminPage] getSession error:", sessionError);
          if (isMounted) {
            setAdminUser(null);
            setAdminProfile(null);
            setAdminVerified(false);
            setProfileResolved(true);
          }
          return;
        }
        if (!sessionData.session) {
          if (isMounted) {
            setAdminUser(null);
            setAdminProfile(null);
            setAdminVerified(false);
            setProfileResolved(true);
          }
          return;
        }
        const currentUser = sessionData.session.user ?? null;
        if (isMounted) setAdminUser(currentUser);
        if (currentUser && isMounted) {
          await fetchAdminProfile(currentUser.id);
        } else {
          if (isMounted) setProfileResolved(true);
        }
      } catch (err) {
        console.warn("[AdminPage] init exception:", err);
        if (isMounted) {
          setAdminUser(null);
          setAdminProfile(null);
          setAdminVerified(false);
          setProfileResolved(true);
        }
      } finally {
        if (isMounted) setAdminLoading(false);
      }
    };
    init();

    const { data: listener } = supabaseAdmin.auth.onAuthStateChange(
      (_event, session) => {
        if (!isMounted) return;
        const currentUser = session?.user ?? null;
        setAdminUser(currentUser);
        if (currentUser) {
          fetchAdminProfile(currentUser.id).catch((e) => {
            console.error("[AdminPage] onAuthStateChange fetchAdminProfile error:", e);
          });
        } else {
          setAdminProfile(null);
          setProfileResolved(true);
        }
      },
    );

    return () => {
      isMounted = false;
      listener.subscription.unsubscribe();
    };
  }, [fetchAdminProfile]);

  // Sync URL when tab changes
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === "verifications") {
      navigate("/admin/verifications", { replace: true });
    } else {
      navigate("/admin", { replace: true });
    }
  };

  // Sync tab when URL changes
  useEffect(() => {
    if (location.pathname === "/admin/verifications") {
      setActiveTab("verifications");
    } else if (location.pathname === "/admin") {
      if (activeTab === "verifications") {
        setActiveTab("overview");
      }
    }
  }, [location.pathname]);

  // Admin login handler — uses isolated supabaseAdmin client
  const handleAdminLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      const { data, error } = await supabaseAdmin.auth.signInWithPassword({
        email: usernameToEmail(loginUsername),
        password: loginPassword,
      });
      if (error) throw error;
      if (data.session?.user) {
        setAdminUser(data.session.user);
        await fetchAdminProfile(data.session.user.id);
      }
    } catch (err: unknown) {
      const rawMsg = err instanceof Error ? err.message : "登入失敗，請重試";
      setLoginError(translateAuthError(rawMsg));
    } finally {
      setLoginLoading(false);
    }
  };

  // Derived admin check
  const isAdmin = adminProfile?.is_admin ?? false;

  // When user data is loaded, check admin status
  useEffect(() => {
    if (!adminLoading && profileResolved) {
      if (adminUser && isAdmin) {
        sessionStorage.setItem("admin_verified", "true");
        setAdminVerified(true);
      } else if (adminUser && !isAdmin) {
        sessionStorage.removeItem("admin_verified");
        setAdminVerified(false);
      } else if (!adminUser) {
        sessionStorage.removeItem("admin_verified");
        setAdminVerified(false);
      }
    }
  }, [adminLoading, profileResolved, adminUser, isAdmin]);

  // Admin sign out — only clears admin session
  const handleAdminSignOut = useCallback(async () => {
    try {
      await supabaseAdmin.auth.signOut();
    } catch (err) {
      console.error("[AdminPage] signOut error:", err);
    }
    sessionStorage.removeItem("admin_verified");
    setAdminUser(null);
    setAdminProfile(null);
    setAdminVerified(false);
    setProfileResolved(true);
  }, []);

  // Loading state — 在異步憑證與權限欄位尚未完全就緒前，持續顯示 Loading，絕不提前彈出無權訪問
  if (adminLoading || !profileResolved) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 rounded-full border-2 border-navy-200 border-t-navy-700 animate-spin" />
          <p className="font-body text-sm text-navy-400">驗證管理員權限中...</p>
        </div>
      </div>
    );
  }

  // Logged in but not admin — forbidden
  if (adminUser && !isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl border border-navy-100 shadow-sm w-full max-w-[400px] p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-forbid-line text-red-500 text-2xl" />
          </div>
          <h1 className="font-display text-lg font-bold text-navy-900">無權訪問</h1>
          <p className="font-body text-sm text-navy-500 mt-2">
            您的帳號沒有管理員權限，無法進入後台。
          </p>
          <div className="flex flex-col gap-2 mt-6">
            <button
              onClick={handleAdminSignOut}
              className="w-full font-body text-sm font-semibold px-4 py-2.5 rounded-lg bg-navy-700 text-white hover:bg-navy-800 cursor-pointer whitespace-nowrap"
            >
              登出並切換帳號
            </button>
            <a
              href="/dashboard"
              className="font-body text-sm px-4 py-2.5 rounded-lg border border-navy-200 text-navy-600 hover:bg-navy-50 inline-block"
            >
              前往使用者中心
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Not logged in — show admin login
  if (!adminUser || !adminVerified) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl border border-navy-100 shadow-sm w-full max-w-[400px] p-8">
          <div className="flex flex-col items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-navy-700 flex items-center justify-center">
              <i className="ri-shield-check-line text-white text-xl" />
            </div>
            <div className="text-center">
              <h1 className="font-display text-lg font-bold text-navy-900">管理員登入</h1>
              <p className="font-body text-xs text-navy-400 mt-1">請使用管理員帳號密碼登入後台</p>
            </div>
          </div>

          <form onSubmit={handleAdminLogin} className="flex flex-col gap-4">
            {loginError && (
              <div className="font-body text-sm px-4 py-2.5 rounded-lg bg-red-50 text-red-600 border border-red-200">
                {loginError}
              </div>
            )}
            <div>
              <label className="font-body text-xs font-medium text-navy-600 block mb-1.5">帳號</label>
              <input
                type="text"
                value={loginUsername}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 12);
                  setLoginUsername(val);
                }}
                className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white"
                placeholder="輸入 4-12 位管理員帳號（英文+數字）"
                required
                minLength={4}
                maxLength={12}
              />
            </div>
            <div>
              <label className="font-body text-xs font-medium text-navy-600 block mb-1.5">密碼</label>
              <input
                type="password"
                value={loginPassword}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 12);
                  setLoginPassword(val);
                }}
                className="w-full font-body text-sm px-3 py-2.5 rounded-lg border border-navy-200 outline-none focus:border-navy-400 bg-white"
                placeholder="輸入 4-12 位密碼（英文+數字）"
                required
                minLength={4}
                maxLength={12}
              />
            </div>
            <button
              type="submit"
              disabled={loginLoading || !loginUsername || !loginPassword || loginUsername.length < 4 || loginPassword.length < 4}
              className="w-full font-body text-sm font-semibold px-4 py-2.5 rounded-lg bg-navy-700 text-white hover:bg-navy-800 disabled:opacity-50 cursor-pointer whitespace-nowrap"
            >
              {loginLoading ? "登入中..." : "登入後台"}
            </button>
          </form>

          <div className="mt-4 text-center">
            <a href="/" className="font-body text-xs text-navy-400 hover:text-navy-600">
              ← 返回首頁
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <AdminSidebar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        adminName={adminProfile?.name || adminUser.email || "管理員"}
        onSignOut={handleAdminSignOut}
      />

      {/* Main content */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="bg-white border-b border-navy-100 px-6 py-3 flex items-center justify-between sticky top-0 z-10">
          <div>
            <h1 className="font-display text-base font-bold text-navy-900">{titleInfo.title}</h1>
            <p className="font-body text-xs text-navy-400">{titleInfo.subtitle}</p>
          </div>
          <div className="flex items-center gap-2">
            <button className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-navy-50 text-navy-400 hover:text-navy-700 cursor-pointer">
              <i className="ri-notification-3-line text-lg" />
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-navy-50 text-navy-400 hover:text-navy-700 cursor-pointer">
              <i className="ri-settings-3-line text-lg" />
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-6 overflow-y-auto">
          {activeTab === "overview" && <AdminOverview onTabChange={handleTabChange} />}
          {activeTab === "supply" && <AdminSupply />}
          {activeTab === "orders" && <AdminOrders />}
          {activeTab === "users" && <AdminUsers />}
          {activeTab === "verifications" && <AdminVerifications />}
          {activeTab === "finance" && <AdminFinance />}
        </main>
      </div>
    </div>
  );
}